"use client";

import { useBooking } from "@/contexts/BookingContext";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { WorkflowFormField } from "@/types/booking";
import { useState, useCallback } from "react";
import { useSubmitWorkflowAnswers } from "@/lib/api/useWorkflowForms";
import {
  CheckSquare,
  Square,
  Circle,
  CheckCircle2,
  AlertCircle,
  Loader2,
  FileText,
} from "lucide-react";

interface WorkflowFormModalProps {
  onSubmit?: () => void;
  onSkip?: () => void;
}

export default function WorkflowFormModal({
  onSubmit,
  onSkip,
}: WorkflowFormModalProps) {
  const {
    workflowForm,
    workflowFormAnswers,
    showWorkflowFormModal,
    workflowFormSubmissionId,
    setShowWorkflowFormModal,
    updateWorkflowFormAnswer,
    clearWorkflowForm,
    serviceClient,
    addToast,
  } = useBooking();

  const { submitAnswers, loading: submitting } = useSubmitWorkflowAnswers();
  const [validationErrors, setValidationErrors] = useState<{
    [key: string]: string;
  }>({});

  // Validate all required fields
  const validateForm = useCallback(() => {
    if (!workflowForm) return true;

    const errors: { [key: string]: string } = {};

    workflowForm.fields.forEach((field) => {
      if (field.required) {
        const answer = workflowFormAnswers[field.id];
        if (!answer || (Array.isArray(answer) && answer.length === 0)) {
          errors[field.id] = `${field.label} is required`;
        }
      }

      // Email validation
      if (
        field.fieldType === "email" &&
        workflowFormAnswers[field.id] &&
        typeof workflowFormAnswers[field.id] === "string"
      ) {
        const email = workflowFormAnswers[field.id] as string;
        if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          errors[field.id] = "Please enter a valid email address";
        }
      }
    });

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [workflowForm, workflowFormAnswers]);

  const handleSubmit = async () => {
    if (!validateForm()) {
      addToast({
        message: "Please fill in all required fields",
        variant: "error",
      });
      return;
    }

    if (!workflowFormSubmissionId || !serviceClient) {
      // No submission ID means we can just close and continue
      setShowWorkflowFormModal(false);
      onSubmit?.();
      return;
    }

    try {
      const result = await submitAnswers({
        submissionId: workflowFormSubmissionId,
        answers: workflowFormAnswers,
        phone: `${serviceClient.callingCode}${serviceClient.phone}`,
        clientId: serviceClient.id || "",
      });

      if (result) {
        addToast({
          message: "Form submitted successfully",
          variant: "success",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      // Don't block booking even if form submission fails
    }

    setShowWorkflowFormModal(false);
    onSubmit?.();
  };

  const handleSkip = () => {
    setShowWorkflowFormModal(false);
    clearWorkflowForm();
    onSkip?.();
  };

  const handleClose = () => {
    setShowWorkflowFormModal(false);
  };

  // Render different field types
  const renderField = (field: WorkflowFormField) => {
    const value = workflowFormAnswers[field.id];
    const error = validationErrors[field.id];

    switch (field.fieldType) {
      case "short_text":
      case "email":
      case "phone":
      case "number":
        return (
          <div key={field.id} className="space-y-2">
            <label className="block text-sm font-medium text-grey-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.description && (
              <p className="text-xs text-grey-500">{field.description}</p>
            )}
            <input
              type={
                field.fieldType === "email"
                  ? "email"
                  : field.fieldType === "number"
                  ? "number"
                  : field.fieldType === "phone"
                  ? "tel"
                  : "text"
              }
              value={(value as string) || ""}
              onChange={(e) =>
                updateWorkflowFormAnswer(field.id, e.target.value)
              }
              placeholder={field.placeholder || ""}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300 ${
                error ? "border-red-500" : "border-grey-200"
              }`}
            />
            {error && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {error}
              </p>
            )}
          </div>
        );

      case "long_text":
        return (
          <div key={field.id} className="space-y-2">
            <label className="block text-sm font-medium text-grey-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.description && (
              <p className="text-xs text-grey-500">{field.description}</p>
            )}
            <textarea
              value={(value as string) || ""}
              onChange={(e) =>
                updateWorkflowFormAnswer(field.id, e.target.value)
              }
              placeholder={field.placeholder || ""}
              rows={4}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300 resize-none ${
                error ? "border-red-500" : "border-grey-200"
              }`}
            />
            {error && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {error}
              </p>
            )}
          </div>
        );

      case "single_choice":
        return (
          <div key={field.id} className="space-y-2">
            <label className="block text-sm font-medium text-grey-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.description && (
              <p className="text-xs text-grey-500">{field.description}</p>
            )}
            <div className="space-y-2">
              {field.options?.map((option) => (
                <label
                  key={option}
                  className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-grey-50 transition-colors ${
                    value === option
                      ? "border-green-300 bg-green-50"
                      : "border-grey-200"
                  }`}
                >
                  <input
                    type="radio"
                    name={field.id}
                    value={option}
                    checked={value === option}
                    onChange={() => updateWorkflowFormAnswer(field.id, option)}
                    className="sr-only"
                  />
                  {value === option ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  ) : (
                    <Circle className="h-5 w-5 text-grey-300 flex-shrink-0" />
                  )}
                  <span className="text-sm text-grey-700">{option}</span>
                </label>
              ))}
            </div>
            {error && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {error}
              </p>
            )}
          </div>
        );

      case "multiple_choice":
        const selectedValues = (value as string[]) || [];
        return (
          <div key={field.id} className="space-y-2">
            <label className="block text-sm font-medium text-grey-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.description && (
              <p className="text-xs text-grey-500">{field.description}</p>
            )}
            <div className="space-y-2">
              {field.options?.map((option) => {
                const isSelected = selectedValues.includes(option);
                return (
                  <label
                    key={option}
                    className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-grey-50 transition-colors ${
                      isSelected
                        ? "border-green-300 bg-green-50"
                        : "border-grey-200"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {
                        const newValues = isSelected
                          ? selectedValues.filter((v) => v !== option)
                          : [...selectedValues, option];
                        updateWorkflowFormAnswer(field.id, newValues);
                      }}
                      className="sr-only"
                    />
                    {isSelected ? (
                      <CheckSquare className="h-5 w-5 text-green-500 flex-shrink-0" />
                    ) : (
                      <Square className="h-5 w-5 text-grey-300 flex-shrink-0" />
                    )}
                    <span className="text-sm text-grey-700">{option}</span>
                  </label>
                );
              })}
            </div>
            {error && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {error}
              </p>
            )}
          </div>
        );

      case "date":
        return (
          <div key={field.id} className="space-y-2">
            <label className="block text-sm font-medium text-grey-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.description && (
              <p className="text-xs text-grey-500">{field.description}</p>
            )}
            <input
              type="date"
              value={(value as string) || ""}
              onChange={(e) =>
                updateWorkflowFormAnswer(field.id, e.target.value)
              }
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300 ${
                error ? "border-red-500" : "border-grey-200"
              }`}
            />
            {error && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {error}
              </p>
            )}
          </div>
        );

      default:
        // Fallback for unknown field types - render as text input
        return (
          <div key={field.id} className="space-y-2">
            <label className="block text-sm font-medium text-grey-700">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {field.description && (
              <p className="text-xs text-grey-500">{field.description}</p>
            )}
            <input
              type="text"
              value={(value as string) || ""}
              onChange={(e) =>
                updateWorkflowFormAnswer(field.id, e.target.value)
              }
              placeholder={field.placeholder || ""}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-300 ${
                error ? "border-red-500" : "border-grey-200"
              }`}
            />
            {error && (
              <p className="text-xs text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                {error}
              </p>
            )}
          </div>
        );
    }
  };

  if (!workflowForm) return null;

  // Sort fields by order if available
  const sortedFields = [...workflowForm.fields].sort(
    (a, b) => (a.order || 0) - (b.order || 0)
  );

  const hasRequiredFields = workflowForm.fields.some((f) => f.required);

  return (
    <Dialog open={showWorkflowFormModal} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col bg-white">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-green-500" />
            <DialogTitle>{workflowForm.name}</DialogTitle>
          </div>
          {workflowForm.description && (
            <DialogDescription className="text-grey-600">
              {workflowForm.description}
            </DialogDescription>
          )}
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {sortedFields.map(renderField)}
        </div>

        <DialogFooter className="flex-shrink-0 border-t pt-4 gap-2">
          {!hasRequiredFields && (
            <Button
              variant="outline"
              onClick={handleSkip}
              className="border-grey-200"
            >
              Skip
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            disabled={submitting}
            className="bg-green-300 hover:bg-green-400 text-white flex-1"
          >
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Submitting...
              </>
            ) : (
              "Continue"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
